import { Clock, History } from 'lucide-react';
import { EmptyState } from '../ui/EmptyState';
import { formatRelativeTime } from '../../utils/format';
import type { JiraIssue } from '../../types';

function ageInDays(created: string): number {
  return Math.floor((Date.now() - new Date(created).getTime()) / 86_400_000);
}

function IssueRow({ issue, meta }: { issue: JiraIssue; meta: string }) {
  return (
    <a
      href={issue.url}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-3 py-2.5 transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/40 -mx-1 px-1 rounded-lg"
    >
      <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-500 dark:bg-slate-800 dark:text-slate-400">
        {issue.key}
      </span>
      <span className="min-w-0 flex-1 truncate text-sm text-slate-700 dark:text-slate-200">{issue.summary}</span>
      <span className="shrink-0 text-xs text-slate-400">{meta}</span>
    </a>
  );
}

export function AgingIssuesPanel({
  staleIssues,
  oldestOpenIssues,
  avgOpenIssueAgeDays,
}: {
  staleIssues: JiraIssue[];
  oldestOpenIssues: JiraIssue[];
  avgOpenIssueAgeDays: number;
}) {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div>
        <div className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-slate-500 dark:text-slate-400">
          <History size={14} /> Longest open
        </div>
        <p className="mb-2 text-xs text-slate-400">
          Average open issue is {avgOpenIssueAgeDays} {avgOpenIssueAgeDays === 1 ? 'day' : 'days'} old
        </p>
        {oldestOpenIssues.length === 0 ? (
          <EmptyState icon={History} title="No open issues" />
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {oldestOpenIssues.map((issue) => (
              <IssueRow key={issue.id} issue={issue} meta={`${ageInDays(issue.created)}d old`} />
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-slate-500 dark:text-slate-400">
          <Clock size={14} /> Stale (14+ days quiet)
        </div>
        <p className="mb-2 text-xs text-slate-400">Open, but nobody has touched these recently</p>
        {staleIssues.length === 0 ? (
          <EmptyState icon={Clock} title="Nothing stale" description="Everything open has recent activity." />
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {staleIssues.map((issue) => (
              <IssueRow key={issue.id} issue={issue} meta={formatRelativeTime(issue.updated)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
